import { useTranslation } from 'react-i18next';
import { MoneyBillWaveLightIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import useUpdateUserCurrency from '@/enterprise/data/useUpdateUserCurrency';
import { useReporting } from '@/reporting';
import useCurrencies from '@/shared/data/useCurrencies';
import DrawerSubmenuOverlay from './DrawerSubmenuOverlay';
import SubmenuDropdownItem from './SubmenuDropdownItem';

interface SubmenuOverlayProps {
  isOpen: boolean;
  onBack: () => void;
  onClose: () => void;
  portalContainer: Element | null;
  shouldShowPortal: boolean;
}

interface CurrencyMenuItem {
  label: string;
  name: string;
  key: string;
  isSelected: boolean;
  onClick: () => void;
}

interface UseCurrencyMenuOptions {
  onSelect?: () => void;
}

const useCurrencyMenu = (options?: UseCurrencyMenuOptions) => {
  const currencies = useCurrencies();
  const { selectedCurrency, setSelectedCurrency } = useCurrencyContext();
  const { trackEnterpriseEvent } = useReporting();
  const { mutate: mutateCurrency } = useUpdateUserCurrency();

  const handleChangeCurrency = (currency: string) => {
    setSelectedCurrency(currency);
    // do not want this to block updating currency
    // if it fails, we fallback to default behavior for currency
    mutateCurrency({ aggregateCurrencyFormat: currency });
    trackEnterpriseEvent('currency::changed', { currency });
    options?.onSelect?.();
  };

  const items: CurrencyMenuItem[] = currencies.map((currency) => ({
    label: currency,
    name: currency,
    key: currency,
    isSelected: selectedCurrency === currency,
    onClick: () => handleChangeCurrency(currency),
  }));

  return {
    currencies,
    selectedCurrency,
    items,
    handleChangeCurrency,
  };
};

export const CurrencyMenuItem = () => {
  const { t } = useTranslation();
  const { selectedCurrency, items } = useCurrencyMenu();

  return (
    <SubmenuDropdownItem
      icon={MoneyBillWaveLightIcon}
      items={items}
      selectedValue={selectedCurrency}
      subtitle={t('core.preferredCurrencyMessage')}
      title={t('core.preferredCurrency')}
      width="w-52"
    />
  );
};

export const CurrencySubmenuOverlay = ({
  isOpen,
  onBack,
  onClose,
  portalContainer,
  shouldShowPortal,
}: SubmenuOverlayProps) => {
  const { t } = useTranslation();
  const { items } = useCurrencyMenu({ onSelect: onClose });

  return (
    <DrawerSubmenuOverlay
      items={items}
      isOpen={isOpen}
      onBack={onBack}
      portalContainer={portalContainer}
      shouldShowPortal={shouldShowPortal}
      subtitle={t('core.preferredCurrencyMessage')}
      title={t('core.preferredCurrency')}
    />
  );
};
